/**
 * 判定ロジックページ (NG/注意/OK の定義 + 衝突解決ルール)
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business レビュー判定3段階の定義・衝突解決ルール・出力フォーマット例の表示と編集。
 * @graph-connects api [calls] judgment.get / judgment.update
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { JudgmentPolicyInput } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import {
  EmptyState,
  Field,
  GhostButton,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
  StatusPill,
} from '../components/ui.js';

/**
 * 判定ロジックページ。
 * @returns ページ要素
 * @graph-connects api [calls] judgment.get / judgment.update
 */
export function JudgmentPage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const policyQuery = useQuery(orpc.judgment.get.queryOptions({ input: {} }));
  const policy = policyQuery.data ?? null;

  const [draft, setDraft] = useState<JudgmentPolicyInput | null>(null);

  const updateMutation = useMutation(
    orpc.judgment.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.judgment.key() });
        setDraft(null);
        showToast('success', '判定ポリシーを更新しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  return (
    <div>
      <SectionHeading
        title="Judgment Logic"
        action={
          auth.canEdit && policy ? (
            <GhostButton
              onClick={() => {
                setDraft({
                  levels: policy.levels.map((level) => ({
                    ...level,
                    outputRequirements: [...level.outputRequirements],
                  })),
                  conflictRules: policy.conflictRules.map((rule) => ({ ...rule })),
                  outputFormatExample: policy.outputFormatExample,
                });
              }}
            >
              編集
            </GhostButton>
          ) : null
        }
      />
      {!policy ? (
        <EmptyState message="判定ポリシーが未登録です (seed 未実行)" />
      ) : (
        <>
          <h3 className="mb-3 text-sm font-bold text-foreground">判定の3段階</h3>
          <div className="mb-6 overflow-x-auto rounded-xl border border-line-light bg-card">
            <table className="w-full text-body">
              <thead>
                <tr>
                  {['判定', '意味', '出力に含めるもの'].map((header) => (
                    <th key={header} className="dh-th">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {policy.levels.map((level) => (
                  <tr key={level.level} className="hover:bg-glass">
                    <td className="dh-td">
                      <StatusPill level={level.level} />
                    </td>
                    <td className="dh-td text-subtle">{level.meaning}</td>
                    <td className="dh-td text-subtle">{level.outputRequirements.join(' / ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mb-3 text-sm font-bold text-foreground">衝突解決ルール</h3>
          {policy.conflictRules.map((rule) => (
            <div key={rule.elementType} className="rule-card">
              <span
                className="rule-bar"
                style={{
                  background: rule.resolution === 'ng_fixed' ? 'var(--dh-ng)' : 'var(--dh-caution)',
                }}
              />
              {rule.description}
            </div>
          ))}

          {policy.outputFormatExample !== '' && (
            <>
              <h3 className="mb-3 mt-6 text-sm font-bold text-foreground">
                判定出力フォーマット例
              </h3>
              <pre className="overflow-x-auto rounded-xl border border-line-light bg-surface p-5 font-mono text-xs leading-loose text-subtle">
                {policy.outputFormatExample}
              </pre>
            </>
          )}
        </>
      )}

      <Modal
        open={draft !== null}
        title="判定ポリシーを編集"
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            {draft.levels.map((level, index) => (
              <Field key={level.level} label={`${level.level.toUpperCase()} の意味`}>
                <input
                  className={INPUT_CLASS}
                  value={level.meaning}
                  onChange={(event) => {
                    const levels = draft.levels.map((current, currentIndex) =>
                      currentIndex === index
                        ? { ...current, meaning: event.target.value }
                        : current,
                    );
                    setDraft({ ...draft, levels });
                  }}
                />
              </Field>
            ))}
            {draft.conflictRules.map((rule, index) => (
              <Field key={rule.elementType} label={`衝突解決: ${rule.elementType}`}>
                <input
                  className={INPUT_CLASS}
                  value={rule.description}
                  onChange={(event) => {
                    const conflictRules = draft.conflictRules.map((current, currentIndex) =>
                      currentIndex === index
                        ? { ...current, description: event.target.value }
                        : current,
                    );
                    setDraft({ ...draft, conflictRules });
                  }}
                />
              </Field>
            ))}
            <Field label="判定出力フォーマット例">
              <textarea
                className={`${INPUT_CLASS} h-40 resize-y font-mono text-xs`}
                value={draft.outputFormatExample}
                onChange={(event) => {
                  setDraft({ ...draft, outputFormatExample: event.target.value });
                }}
              />
            </Field>
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setDraft(null);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={updateMutation.isPending}
                onClick={() => {
                  updateMutation.mutate(draft);
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
